import React, { useContext, useEffect } from 'react';
import { useParams, Link, useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

import { useLayout } from '~hooks/useLayout';
import { useSeo } from '~hooks/useSeo';
import { UserContext } from '~contexts/user';
import { useOpenChat } from '~hooks/useOpenChat';
import PaymentActions from '~redux/payment';
import * as REST from '~services/rest';

import Button from '~components/button';
import NotFound from '~components/not-found';
import FullLoader from '~components/loader/full-loader';

import ChatContainer from './chat-container';
import useDiscussion from './useDiscussion';

import './style.scss';

const Discussion = () => {
  useSeo();
  const dispatch = useDispatch();
  const intl = useIntl();
  const history = useHistory();
  const { getAccessTokenSilently } = useAuth0();

  const { recipientId } = useParams();
  const { user, getCustomer, getWorker } = useContext(UserContext);

  const {
    isNotFound,
    recipient,
    isLoading,
    isLoadingCanChat,
    canChat,
    chatBlockedReason,
    chatCheckFailed,
    dispatch: dispatchLocal,
  } = useDiscussion();
  useLayout({ type: canChat ? 'no-header-on-mobile' : 'full', displayFooter: false }, [canChat]);

  const { canOpenChat } = useOpenChat({
    personId: recipientId,
    locationId: user.accountType === 'client' ? user.wish?.address?.place_id : null,
  });

  const retryPayment = () => {
    if (chatCheckFailed) {
      dispatchLocal({ type: 'loading' });
      window.location.reload();
      return;
    }
    dispatch(PaymentActions.setModalData(recipientId, user.wish?.address?.place_id, chatBlockedReason));
    dispatch(PaymentActions.toggleRequestModal(true));
  };

  const getUser = async (accountType) => {
    let r = null;
    if (accountType === 'worker') {
      r = await getCustomer(recipientId);
    } else {
      r = await getWorker(recipientId);
    }
    return r;
  };

  useEffect(async () => {
    try {
      dispatchLocal({ type: 'loading' });
      let r = null;
      try {
        r = await getUser(user.accountType);
      } catch (err) {
        // If getUser fails, it's possible that recipientId is a chatId (e.g. from an email link)
        const token = await getAccessTokenSilently();
        const result = await REST.get({
          name: `chats/`,
          mockFileName: 'chats/get-chats',
          shouldThrowError: true,
          params: {
            'participant-id': user.personId,
          },
          token,
        });
        const matchedChat = result.data?.find((d) => String(d.id) === String(recipientId));
        if (matchedChat) {
          const otherParticipant = matchedChat.participants?.find((p) => p.id !== user.personId);
          if (otherParticipant) {
            history.replace({
              pathname: `/discussion/${otherParticipant.id}`,
              search: history.location.search,
            });
            return;
          }
        }
        throw err;
      }
      dispatchLocal({ type: 'success_recipient', recipient: r });
    } catch (err) {
      dispatchLocal({ type: 'failure_recipient' });
    }
  }, [recipientId]);

  useEffect(async () => {
    if (recipient) {
      const { allowed, reason, failed } = await canOpenChat({ pId: recipient.personId, aType: recipient.accountType });
      dispatchLocal({ type: 'can_chat', canChat: allowed, reason, failed });
    }
  }, [recipient]);

  return isNotFound ? (
    <NotFound
      headline={intl.formatMessage({
        id: 'containers.chat.not-found.headline',
      })}
      content={intl.formatMessage({
        id: 'containers.chat.not-found.content',
      })}
    />
  ) : isLoading || isLoadingCanChat ? (
    <FullLoader />
  ) : canChat ? (
    <div className="c-account__main l-container c-discussion-wrapper">
      <ChatContainer currentUser={user} recipient={recipient} />
    </div>
  ) : (
    <div className="c-account__main l-container c-get-access-wrapper">
      <p className="c-get-access__explanation">
        {chatCheckFailed
          ? 'We kunnen je chattoegang tijdelijk niet controleren. Probeer het opnieuw; je abonnement is niet gewijzigd.'
          : intl.formatMessage({
              id: 'containers.chat.get-access-explanation',
              defaultMessage: 'You need an active subscription to continue this conversation with this helper.',
            })}
      </p>
      <Button onClick={retryPayment}>
        {chatCheckFailed
          ? 'Opnieuw proberen'
          : intl.formatMessage({
              id: 'containers.chat.get-access',
              defaultMessage: 'Get access',
            })}
      </Button>
      <Link to={`/profile/${recipient?.slug || recipientId}`} className="c-btn c-btn--secondary">
        {user?.accountType === 'worker'
          ? intl.formatMessage({
              id: 'containers.chat.back-to-customer-profile',
              defaultMessage: 'Back to customer profile',
            })
          : intl.formatMessage({
              id: 'containers.chat.back-to-worker-profile',
              defaultMessage: 'Back to worker profile',
            })}
      </Link>
    </div>
  );
};

export default Discussion;
